import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import {
  CheckCircle,
  XCircle,
  Clock,
  ArrowLeft,
  ArrowRight,
  Award,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuizPage = () => {
  const { quizId } = useParams();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    startQuiz();
  }, [quizId]);

  // Timer
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || result) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          submitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timeLeft, result]);

  const startQuiz = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await axios.post(`${API_URL}/elearning/quizzes/${quizId}/start`);
      setQuiz(res.data.quiz);
      setQuestions(res.data.questions);
      
      if (res.data.quiz.time_limit_minutes) {
        setTimeLeft(res.data.quiz.time_limit_minutes * 60);
      }
      
    } catch (error) {
      console.error('Error starting quiz:', error);
      setError(error.response?.data?.detail || 'Erreur lors du démarrage du quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const submitQuiz = async () => {
    if (submitting) return;
    
    try {
      setSubmitting(true);
      
      const formattedAnswers = questions.map(q => ({
        question_id: q.id,
        answer: answers[q.id] || ''
      }));
      
      const res = await axios.post(`${API_URL}/elearning/quizzes/${quizId}/submit`, {
        answers: formattedAnswers
      });
      
      setResult(res.data);
      
    } catch (error) {
      console.error('Error submitting quiz:', error);
      setError('Erreur lors de la soumission du quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentQuestion = questions[currentQuestionIndex];
  const answeredCount = Object.keys(answers).length;
  const progressPercent = (answeredCount / questions.length) * 100;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !quiz) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold text-gray-900 mb-2">
              Erreur
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate(-1)}>
              Retour
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show results
  if (result) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="quiz-results">
        <div className="max-w-2xl mx-auto">
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className={`p-8 text-center text-white ${result.passed ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-red-500 to-red-600'}`}>
              {result.passed ? (
                <>
                  <Award className="w-20 h-20 mx-auto mb-4" />
                  <h1 className="font-heading text-3xl font-bold mb-2">Félicitations !</h1>
                  <p className="text-white/90">Vous avez réussi le quiz</p>
                </>
              ) : (
                <>
                  <XCircle className="w-20 h-20 mx-auto mb-4" />
                  <h1 className="font-heading text-3xl font-bold mb-2">Quiz non réussi</h1>
                  <p className="text-white/90">Vous pouvez réessayer</p>
                </>
              )}
            </div>
            
            <CardContent className="p-8">
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <p className="text-3xl font-bold text-primary">{result.percentage}%</p>
                  <p className="text-sm text-gray-500">Score</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <p className="text-3xl font-bold text-secondary">{result.score}/{result.max_score}</p>
                  <p className="text-sm text-gray-500">Points</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <p className="text-3xl font-bold text-accent">{quiz?.passing_score}%</p>
                  <p className="text-sm text-gray-500">Minimum requis</p>
                </div>
              </div>

              {/* Answers breakdown */}
              <h3 className="font-heading font-bold text-gray-900 mb-4">Détail des réponses</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {result.answers_detail?.map((answer, index) => (
                  <div 
                    key={index} 
                    className={`p-4 rounded-lg border ${answer.is_correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                  >
                    <div className="flex items-start gap-3">
                      {answer.is_correct ? (
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm">{answer.question_text}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          Votre réponse: <span className={answer.is_correct ? 'text-green-600' : 'text-red-600'}>{answer.given_answer || 'Non répondu'}</span>
                        </p>
                        {!answer.is_correct && (
                          <p className="text-sm text-green-600 mt-1">
                            Bonne réponse: {answer.correct_answer}
                          </p>
                        )}
                        {answer.explanation && (
                          <p className="text-xs text-gray-500 mt-2 italic">{answer.explanation}</p>
                        )}
                      </div>
                      <span className="text-sm font-medium">
                        {answer.points_earned}/{answer.points_possible}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-4 mt-8">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => navigate(-1)}
                >
                  Retour au cours
                </Button>
                {!result.passed && (
                  <Button 
                    className="flex-1"
                    onClick={() => {
                      setResult(null);
                      setAnswers({});
                      setCurrentQuestionIndex(0);
                      startQuiz();
                    }}
                  >
                    Réessayer
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="quiz-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading font-bold text-gray-900">{quiz?.title}</h1>
              <p className="text-sm text-gray-500">
                Question {currentQuestionIndex + 1} sur {questions.length}
              </p>
            </div>
            
            {timeLeft !== null && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${timeLeft < 60 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}>
                <Clock className="w-5 h-5" />
                <span className="font-mono font-bold">{formatTime(timeLeft)}</span>
              </div>
            )}
          </div>
          
          {/* Progress bar */}
          <div className="mt-4">
            <Progress value={progressPercent} className="h-2" />
            <p className="text-xs text-gray-500 mt-1">{answeredCount}/{questions.length} répondu(es)</p>
          </div>
        </div>
      </div>

      {/* Question */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {currentQuestion && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-6">
                <span className="flex-shrink-0 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold">
                  {currentQuestionIndex + 1}
                </span>
                <div className="flex-1">
                  <h2 className="font-heading text-xl font-bold text-gray-900">
                    {currentQuestion.question_text}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">{currentQuestion.points} point(s)</p>
                </div>
              </div>

              {/* Answer options based on question type */}
              <div className="space-y-3 ml-14">
                {currentQuestion.question_type === 'mcq' && currentQuestion.options?.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(currentQuestion.id, String(index))}
                    className={`w-full p-4 text-left rounded-lg border-2 transition-colors ${
                      answers[currentQuestion.id] === String(index)
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        answers[currentQuestion.id] === String(index)
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="text-gray-900">{option}</span>
                    </div>
                  </button>
                ))}

                {currentQuestion.question_type === 'true_false' && (
                  <div className="flex gap-4">
                    {['true', 'false'].map((value) => (
                      <button
                        key={value}
                        onClick={() => handleAnswer(currentQuestion.id, value)}
                        className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                          answers[currentQuestion.id] === value
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="font-medium">
                          {value === 'true' ? 'Vrai' : 'Faux'}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {currentQuestion.question_type === 'text' && (
                  <textarea
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                    placeholder="Tapez votre réponse ici..."
                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-primary focus:ring-0 min-h-32 resize-none"
                  />
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
            disabled={currentQuestionIndex === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Précédent
          </Button>

          {/* Question dots */}
          <div className="flex gap-2 overflow-x-auto max-w-xs">
            {questions.map((q, index) => (
              <button
                key={q.id}
                onClick={() => setCurrentQuestionIndex(index)}
                className={`w-8 h-8 rounded-full flex-shrink-0 text-sm font-medium transition-colors ${
                  index === currentQuestionIndex
                    ? 'bg-primary text-white'
                    : answers[q.id]
                      ? 'bg-green-100 text-green-600'
                      : 'bg-gray-100 text-gray-600'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>

          {currentQuestionIndex < questions.length - 1 ? (
            <Button
              onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
            >
              Suivant
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={submitQuiz}
              disabled={submitting}
              className="bg-green-500 hover:bg-green-600"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Terminer le quiz
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizPage;
